import React, { useEffect, useState, useRef } from "react";
import {
  Box, Typography, Button, Paper, Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Grid from "@mui/material/Grid2";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import StepLayout from "../../../../components/StepsLayout/StepsLayout";
import default_theme from "assets/themes/themes";
import { darken } from "@mui/material/styles";
import {
  fetchProjectDetailRequest,
  fetchProjectStakeholdersRequest,
  saveProjectStakeholdersRequest,
  updateStakeholderPositionRequest,
  deleteProjectStakeholderRequest,
} from "../../../../redux/projects/projectsSlice";

import { fetchRddsRequest } from "../../../../redux/rdds/rddSlice";
import { fetchLocalTaskForceRolesRequest } from "../../../../redux/localTaskForceRoles/localTaskForceRoleSlice";
import { fetchDomainsRequest } from "../../../../redux/domains/domainSlice";
import TooltipLabel from "components/Common/TooltipLabel";
import PrimaryOutlinedButton from "components/Common/PrimaryOutlinedButton";
import DangerButton from "components/Common/DangerButton";
import StepOneDialog from "components/StepOnePage/StepOneDialog";
import StakeholderWhiteboard from "components/StepOnePage/StakeholderWhiteboard";
import styles from "../StepOne/StepOnePage.module.css"
import PhaseArrow from "components/Common/PhaseArrow";
import FigmaBlueDivider from "components/StepComponents/FigmaBlueDivider"
import AddButton from "components/Common/AddButton";
import LocalTaskForceSection from "components/StepOnePage/LocalTaskForceSection";

const StepOnePage = () => {
  const dispatch = useDispatch();
  const { projectId } = useParams();

  const { currentProjectDetail, detailLoading } =
    useSelector((state) => state.projects);

  const { items: rdds } = useSelector((state) => state.rdds);
  const { items: roles } = useSelector(
    (state) => state.localTaskForceRoles
  );
  const domains = useSelector((state) => state.domains.items);

  const [stakeholders, setStakeholders] = useState([]);

  const [openModal, setOpenModal] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [formData, setFormData] = useState(null);
  const [openGreuPopup, setOpenGreuPopup] = useState(false);
  const [openTransversalPopup, setOpenTransversalPopup] = useState(false);
  const [openInterestPopup, setOpenInterestPopup] = useState(false);
  const [openInfluencePopup, setOpenInfluencePopup] = useState(false);
  const [openInclusionPopup, setOpenInclusionPopup] = useState(false);
  const [openLtfWhyPopup, setOpenLtfWhyPopup] = useState(false);
  const [openLtfCompositionPopup, setOpenLtfCompositionPopup] = useState(false);
  const getLevel = (value) => (value >= 50 ? "High" : "Low");


  // =========================
  // FETCHES
  // =========================
  useEffect(() => {
    dispatch(fetchDomainsRequest());
    dispatch(fetchRddsRequest());
    dispatch(fetchLocalTaskForceRolesRequest());
  }, [dispatch]);

  useEffect(() => {
    if (!currentProjectDetail || currentProjectDetail.id !== projectId) {
      dispatch(fetchProjectDetailRequest({ projectId }));
    }
  }, [dispatch, projectId]);

  useEffect(() => {
    if (projectId) {
      dispatch(fetchProjectStakeholdersRequest({ projectId }));
    }
  }, [dispatch, projectId]);

  // =========================
  // PREFILL
  // =========================
  useEffect(() => {
    if (currentProjectDetail?.stakeholders) {
      const data = currentProjectDetail.stakeholders.map((s) => ({
        id: s.id,
        first_name: s.first_name || "",
        last_name: s.last_name || "",
        is_local_task_force_member:
          s.is_local_task_force_member || false,
        local_task_force_role_id:
          s.local_task_force_role_id || null,
        contact_details: s.contact_details || "",
        brief_description: s.brief_description || "",
        rdd_id: s.rdd_id || null,
        domain_id: s.domain_id || null,
        age: s.age || "",
        gender: s.gender || "",
        grue: s.grue || "",
        local_community_trainers:
          s.local_community_trainers || "",
        cross_cutting_priority:
          s.cross_cutting_priority || "",
        interest:
          s.interest !== null && s.interest !== undefined
            ? s.interest
            : 50,
        influence:
          s.influence !== null && s.influence !== undefined
            ? s.influence
            : 50,
      }));

      setStakeholders(data);
    }
  }, [currentProjectDetail]);

  // =========================
  // FORM HANDLERS
  // =========================
  const handleChange = (index, field, value) => {
    setStakeholders((prev) =>
      prev.map((s, i) =>
        i === index ? { ...s, [field]: value } : s
      )
    );
  };

  const handleAdd = () => {
    setFormData({
      id: null,
      first_name: "",
      last_name: "",
      is_local_task_force_member: false,
      local_task_force_role_id: null,
      contact_details: "",
      brief_description: "",
      rdd_id: null,
      domain_id: null,
      age: "",
      gender: "",
      grue: "",
      local_community_trainers: "",
      cross_cutting_priority: "",
      interest: 50,
      influence: 50,
    });

    setCurrentIndex(null);
    setOpenModal(true);
  };

  const handleAddLocalTaskForceMember = () => {
    setFormData({
      id: null,
      first_name: "",
      last_name: "",
      is_local_task_force_member: true,
      local_task_force_role_id: null,
      contact_details: "",
      brief_description: "",
      rdd_id: null,
      domain_id: null,
      age: "",
      gender: "",
      grue: "",
      local_community_trainers: "",
      cross_cutting_priority: "",
      interest: 50,
      influence: 50,
    });

    setCurrentIndex(null);
    setOpenModal(true);
  };

  const handleDeleteStakeholder = (stakeholderId) => {
    // 1. remove from UI immediately (optional)
    setStakeholders(prev =>
      prev.filter(s => s.id !== stakeholderId)
    );

    // 2.CALL BACKEND DELETE
    dispatch(deleteProjectStakeholderRequest({
      projectId,
      stakeholderId,
    }));
  };

  const handleSave = () => {
    dispatch(
      saveProjectStakeholdersRequest({
        projectId,
        stakeholders: stakeholders.map((s) => ({
          ...s,
          age: s.age === "" ? null : Number(s.age),
          gender: s.gender === "" ? null : s.gender,
          contact_details: s.contact_details || null,
          brief_description: s.brief_description || null,
          grue: s.grue || null,
          local_community_trainers:
            s.local_community_trainers || null,
          cross_cutting_priority:
            s.cross_cutting_priority || null,
          interest: s.interest ?? null,
          influence: s.influence ?? null,
        })),
      })
    );
  };

  if (detailLoading || !currentProjectDetail) {
    return <div>Loading...</div>;
  }

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveModal = () => {
    let updated;

    if (currentIndex !== null) {
      updated = stakeholders.map((s, i) =>
        i === currentIndex ? formData : s
      );
    } else {
      updated = [...stakeholders, formData];
    }

    setStakeholders(updated);

    // SAVE TO BACKEND IMMEDIATELY
    dispatch(
      saveProjectStakeholdersRequest({
        projectId,
        stakeholders: updated.map((s) => ({
          ...s,
          age: s.age === "" ? null : Number(s.age),
          gender: s.gender === "" ? null : s.gender,
          contact_details: s.contact_details || null,
          brief_description: s.brief_description || null,
          grue: s.grue || null,
          local_community_trainers:
            s.local_community_trainers || null,
          cross_cutting_priority:
            s.cross_cutting_priority || null,
          interest: s.interest ?? null,
          influence: s.influence ?? null,
        })),
      })
    );

    setOpenModal(false);
  };

  const tagStyle = {
    px: 1.2,
    py: 0.4,
    borderRadius: 2,
    backgroundColor: "#F5F7FA",
    border: "1px solid #D0D5DD",
  };

  const textStyle = {
    fontSize: "0.75rem",
    fontWeight: 600,
    color: "#344054",
  };

  const sortedStakeholders = [...stakeholders].sort(
    (a, b) => (b.interest + b.influence) - (a.interest + a.influence)
  );

  const topStakeholders = sortedStakeholders.filter(
    (s) => s.influence >= 50
  );

  const bottomStakeholders = sortedStakeholders.filter(
    (s) => s.influence < 50
  );

  const localTaskForceMembers = stakeholders.filter(
    (s) => s.is_local_task_force_member
  );

  return (
    <StepLayout step={1}>
      <Box sx={{ textAlign: "center" }}>
        <Typography variant="h5" className={styles.projectTitle}>
          Step 1: Stakeholder Identification
        </Typography>
      </Box>

      <Box sx={{ mt: 4, maxWidth: 900, mx: "auto" }}>

        {/* Introduction */}
        <Typography variant="h5" className={styles.introSubtitle}>
          Identify the people who can contribute to your RIE
        </Typography>

        <Typography variant="body1" className={styles.introText}>
          Your Rural Innovation Ecosystem depends on the people and organisations who
          bring knowledge, experience, resources and different perspectives to the
          development process.
        </Typography>

        <Typography variant="body1" className={styles.introText}>
          In this step, you will brainstorm, identify and analyse stakeholders who
          could contribute to or benefit from your RIE, and then identify the
          stakeholders who should form the Local Task Force.
        </Typography>

        <Box className={styles.checklist}>
          <Typography variant="h6" className={styles.checklistTitle}>
            In this step you will
          </Typography>
          {[
            "Brainstorm stakeholders",
            "Analyse & organise stakeholder profiles",
            "Establish the Local Task Force",
          ].map((item) => (
            <Box className={styles.checkItem} key={item}>
              <CheckCircleOutlineIcon className={styles.checkIcon} />
              <Typography className={styles.checkText}>{item}</Typography>
            </Box>
          ))}
        </Box>

        <FigmaBlueDivider />
        <Box
          sx={{
            mt: 2,
            p: 2,
            mb: 2,
            borderRadius: "8px",
            backgroundColor: "#f5f7fb",
            borderLeft: `4px solid ${default_theme.palette.other.figmaBlue}`,
          }}
        >
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontStyle: "italic",
              color: "text.secondary",
              lineHeight: 1.6,
            }}
          >
            <strong>Note:</strong> Please consider privacy when entering stakeholder information.
            This page is restricted to project owners and collaborators. While access is limited, please ensure you do not upload or share personal, confidential, or sensitive information.
          </Typography>
        </Box>

        {/* =========================
           PHASE 1.1 — BRAINSTORMING
        ========================= */}
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, mb: 1, color: "#23576B" }}
        >
          <PhaseArrow />
          Phase 1.1 — Brainstorming
        </Typography>

        <Typography variant="h5" sx={{ fontWeight: 700, color: "#23576B", mb: 2 }}>
          Who should be involved?
        </Typography>

        <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
          Before building your stakeholder list, think broadly about the people,
          organisations and groups connected to your selected RDDs and territory.
        </Typography>

        <Typography variant="body1" sx={{ color: "#7898A4", mb: 3 }}>
          The aim is to identify a diverse range of stakeholders, including people
          who may be directly involved, affected by the challenges you want to
          address, or able to contribute knowledge, resources or influence.
        </Typography>

        {/* ── INFO PANEL (learn) ── */}
        <Box className={styles.phaseInfoPanel}>
          <Box className={styles.phaseSectionLabel}>
            <Box className={styles.phaseSectionDot} />
            <Box>
              <Typography className={styles.phaseSectionTitle}>Learn</Typography>
              <Typography className={styles.phaseSectionSubtitle}>
                How to identify stakeholders
              </Typography>
            </Box>
          </Box>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 3 }}>
            For a comprehensive stakeholder list, consider your selected RDDs,
            different stakeholder domains and groups that may be underrepresented.
          </Typography>

          {/* Step 1 */}
          <Box className={styles.stepCard}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#23576B", mb: 0.75 }}>
              Start with your selected RDDs
            </Typography>
            <Typography variant="body1" sx={{ color: "#7898A4", mb: 1 }}>
              Think about the stakeholders connected to each of your selected Rural
              Development Drivers.
            </Typography>
            <Typography variant="body1" sx={{ color: "#7898A4", mb: 1 }}>
              Some stakeholders may be relevant to one specific RDD, while others may
              be transversal and relevant to several RDDs.
            </Typography>
            <Button
              onClick={() => setOpenTransversalPopup(true)}
              sx={{
                textTransform: "none",
                color: "#35B3AB",
                fontWeight: 600,
                p: 0,
                mt: 0.5,
                "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
              }}
            >
              What are transversal stakeholders? →
            </Button>
          </Box>

          {/* Step 2 */}
          <Box className={styles.stepCard}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#23576B", mb: 0.75 }}>
              Explore four stakeholder domains
            </Typography>
            <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
              Use these four domains to make sure you consider different parts of the
              local ecosystem:
            </Typography>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" }, gap: 1.5, mb: 2 }}>
              {[
                { label: "Policy", desc: "Public institutions, authorities and organisations involved in policies, planning and local development." },
                { label: "Research", desc: "Universities, research organisations, educational institutions and other knowledge providers." },
                { label: "Industry / Services / Investors", desc: "Businesses, service providers, investors and other economic actors." },
                { label: "Public / User / Community", desc: "Citizens, community organisations, civil society groups, users and other local actors." },
              ].map((domain) => (
                <Box
                  key={domain.label}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid rgba(53, 179, 171, 0.2)",
                    background: "rgba(53, 179, 171, 0.04)",
                  }}
                >
                  <Typography sx={{ fontWeight: 600, color: "#23576B", fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5 }}>
                    {domain.label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#7898A4" }}>
                    {domain.desc}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Typography variant="body1" sx={{ color: "#7898A4" }}>
              These domains help you systematically explore potential stakeholders
              rather than relying only on the people and organisations you already know.
            </Typography>
          </Box>

          {/* Step 3 */}
          <Box className={styles.stepCard}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#23576B", mb: 0.75 }}>
              Think about inclusion
            </Typography>
            <Typography variant="body1" sx={{ color: "#7898A4", mb: 1 }}>
              Your stakeholder list should represent the diversity of your rural community.
            </Typography>
            <Typography variant="body1" sx={{ color: "#7898A4", mb: 1 }}>
              Pay particular attention to Groups at Risk of Exclusion (GREU) and groups
              that may be underrepresented in decision-making or previous development
              initiatives.
            </Typography>
            <Typography variant="body1" sx={{ color: "#7898A4", mb: 1 }}>
              Some groups may not have shown interest in similar projects before because
              they may not have received relevant information or may face barriers to
              participation. Don't assume that a group is not relevant simply because it
              has not participated previously.
            </Typography>
            <Button
              onClick={() => setOpenGreuPopup(true)}
              sx={{
                textTransform: "none",
                color: "#35B3AB",
                fontWeight: 600,
                p: 0,
                mt: 0.5,
                "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
              }}
            >
              What are Groups at Risk of Exclusion (GREU)? →
            </Button>
          </Box>

          {/* Step 4 */}
          <Box className={styles.stepCard}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#23576B", mb: 0.75 }}>
              Use different sources
            </Typography>
            <Typography variant="body1" sx={{ color: "#7898A4", mb: 1 }}>
              You don't need to build your list from scratch. Consider:
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {[
                "Previous collaborations",
                "Existing stakeholder or sector databases",
                "Internet and social media",
                "Local multipliers and influential local actors",
                "Previous innovation projects in the territory",
                "Existing initiatives related to your selected RDDs",
              ].map((item) => (
                <Box component="li" key={item} sx={{ mb: 0.5 }}>
                  <Typography variant="body1" sx={{ color: "#7898A4" }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box className={styles.phaseDataPanel} sx={{ mt: 4 }}>
          <Box className={styles.phaseSectionLabel}>
            <Box className={styles.phaseSectionDot} />
            <Box>
              <Typography className={styles.phaseSectionTitle}>Entry</Typography>
              <Typography className={styles.phaseSectionSubtitle}>
                Build your stakeholder list
              </Typography>
            </Box>
          </Box>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 3 }}>
            Add the stakeholders that could be relevant to your RIE. Don't worry
            about prioritising them yet. The next phases will help you analyse and
            prioritise your list.
          </Typography>

          <Typography
            sx={{
              mb: 2,
              color: "#7898A4",
              fontWeight: 500,
            }}
          >
            Total stakeholders: {stakeholders.length}
          </Typography>

        <Box
          sx={{
            maxHeight: 550,
            overflowY: "auto",
            pr: 1,
          }}
        >
          <Grid container spacing={2}>
            {stakeholders.map((s, index) => {
              const domain = domains.find(
                (d) => String(d.id) === String(s.domain_id)
              );
              const rdd = rdds.find(
                (r) => String(r.id) === String(s.rdd_id)
              );

              const strongDomainColor = domain?.color
                ? darken(domain.color, 0.2)
                : "#23576B";

              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={s.id || index}>
                  <Paper
                    sx={{
                      borderRadius: 4,
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      border: "1px solid #E6EEF2",
                      transition: "all 0.2s ease",
                      background: "white",

                      "&:hover": {
                        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                        transform: "translateY(-3px)",
                      },
                    }}
                  >

                    {/* HEADER */}
                    <Box
                      sx={{
                        position: "relative",
                        p: 2,
                        pb: 1.5,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        borderBottom: "1px solid #F0F4F7",
                      }}
                    >
                      {/* TOP ROW */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "1.05rem",
                            color: "#23576B",
                            lineHeight: 1.3,
                          }}
                        >
                          {`${s.first_name || ""} ${s.last_name || ""}`.trim() ||
                            "Unnamed stakeholder"}
                        </Typography>

                        {/* GRUE BADGE */}
                        {Boolean(s.grue?.trim()) && (
                          <Box
                            sx={{
                              background: "linear-gradient(135deg, #E53935, #FF6B6B)",
                              color: "#fff",
                              px: 1.2,
                              py: 0.4,
                              borderRadius: 5,
                              fontSize: "0.7rem",
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                            }}
                          >
                            GRUE
                          </Box>
                        )}
                      </Box>

                      {/* RDD */}
                      {rdd && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.2,
                          }}
                        >
                          {rdd.image_url && (
                            <Box
                              component="img"
                              src={rdd.image_url}
                              alt={rdd.name}
                              sx={{
                                width: 28,
                                height: 28,
                                objectFit: "contain",
                              }}
                            />
                          )}

                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: "0.85rem",
                              color: rdd.color || "#666",
                            }}
                          >
                            {rdd.name}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* BODY */}
                    <Box
                      sx={{
                        p: 2.5,
                        pt: 2,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                        flex: 1,
                      }}
                    >
                      {/* META INFO */}
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                        }}
                      >
                        {/* DOMAIN */}
                        {domain && (
                          <Box sx={{ ...tagStyle, display: "flex", alignItems: "center", gap: 0.8 }}>
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                backgroundColor: "#344054",
                              }}
                            />
                            <Typography sx={textStyle}> Domain: {domain.name}</Typography>
                          </Box>
                        )}

                        {s.age && (
                          <Box sx={tagStyle}>
                            <Typography sx={textStyle}>Age: {s.age}</Typography>
                          </Box>
                        )}

                        {s.gender && (
                          <Box sx={tagStyle}>
                            <Typography sx={textStyle}>Gender: {s.gender}</Typography>
                          </Box>
                        )}

                        {s.cross_cutting_priority && (
                          <Box sx={tagStyle}>
                            <Typography sx={textStyle}>CCP: {s.cross_cutting_priority}</Typography>
                          </Box>
                        )}
                      </Box>

                      {/* ACTIONS */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          mt: "auto",
                        }}
                      >
                        <Button
                          onClick={() => {
                            setFormData({ ...s });
                            setCurrentIndex(index);
                            setOpenModal(true);
                          }}
                          sx={{
                            flex: 1,
                            backgroundColor: "#23576B",
                            color: "#fff",
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,

                            "&:hover": {
                              backgroundColor: "#1B4454",
                            },
                          }}
                        >
                          View details
                        </Button>

                        <DangerButton
                          onClick={() => handleDeleteStakeholder(s.id)}
                        >
                          Delete
                        </DangerButton>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}


          </Grid>
        </Box>

        <Box sx={{ mt: 1, mb: 0.5, borderTop: "1px solid rgba(35, 87, 107, 0.1)" }} />

        <Box sx={{ pt: 2, display: "flex", justifyContent: "flex-end" }}>
          <AddButton onClick={handleAdd}>
            + Add stakeholder
          </AddButton>
        </Box>

        </Box>

        {/* =========================
           POPUP
        ========================= */}
        <StepOneDialog
          open={openModal}
          onClose={() => setOpenModal(false)}
          onSave={handleSaveModal}
          formData={formData}
          setFormData={setFormData}
          currentIndex={currentIndex}
          rdds={rdds}
          domains={domains}
        />

        <FigmaBlueDivider />

        {/* =========================
           PHASE 1.2 — ANALYSING STAKEHOLDERS (Info Panel)
        ========================= */}
        <Box sx={{ mt: 6 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: "#23576B" }}>
            <PhaseArrow />
            Phase 1.2 — Analysing Stakeholders
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 700, color: "#23576B", mb: 2 }}>
            Understand your stakeholders
          </Typography>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            You have identified your initial stakeholder landscape. Now, look more closely
            at each stakeholder to understand why they are relevant to your RIE, what they
            could contribute, and how they could benefit from participating.
          </Typography>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 3 }}>
            You will then assess each stakeholder according to Interest, Influence and
            Inclusion priority — the three dimensions of the RURACTIVE Three-I Matrix.
            This analysis will help you understand which stakeholders may need to be
            engaged more actively in the RIE.
          </Typography>

          {/* ── INFO PANEL (learn) ── */}
          <Box className={styles.analyseIntroPanel}>
            <Box className={styles.phaseSectionLabel}>
              <Box className={styles.phaseSectionDot} />
              <Box>
                <Typography className={styles.phaseSectionTitle}>Learn</Typography>
                <Typography className={styles.phaseSectionSubtitle}>
                  How to analyse your stakeholders
                </Typography>
              </Box>
            </Box>

            <Typography variant="body1" sx={{ color: "#7898A4", mb: 3 }}>
              Before assessing each stakeholder, consider two perspectives.
            </Typography>

            {/* Two perspective cards */}
            <Box className={styles.perspectiveGrid}>
              <Box className={styles.perspectiveCard}>
                <Typography variant="h6" className={styles.perspectiveCardTitle}>
                  Why do we want to involve them?
                </Typography>
                <Typography variant="body2" className={styles.perspectiveCardText}>
                  Think about:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {[
                    "What expertise or knowledge could they contribute?",
                    "What resources, skills or connections could they bring?",
                    "Are they particularly relevant to one or more RDDs?",
                    "Could they bring the perspective of a group at risk of exclusion or underrepresentation?",
                    "Could their involvement strengthen the RIE or its activities?",
                  ].map((item) => (
                    <Box component="li" key={item} sx={{ mb: 0.5 }}>
                      <Typography variant="body2" className={styles.perspectiveCardText}>
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              <Box className={styles.perspectiveCard}>
                <Typography variant="h6" className={styles.perspectiveCardTitle}>
                  Why might they want to participate?
                </Typography>
                <Typography variant="body2" className={styles.perspectiveCardText}>
                  Think about:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                  {[
                    "How could the RIE benefit them?",
                    "Could they benefit from the solutions developed through the process?",
                    "Do they have a specific interest in one or more RDDs?",
                    "Are they already active in the community?",
                    "Could participation provide them with useful knowledge, connections or opportunities?",
                  ].map((item) => (
                    <Box component="li" key={item} sx={{ mb: 0.5 }}>
                      <Typography variant="body2" className={styles.perspectiveCardText}>
                        {item}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

            <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
              Understanding both sides helps you identify the value and potential
              contribution of each stakeholder before deciding how they should be involved.
            </Typography>

            <Typography variant="h6" sx={{ fontWeight: 700, color: "#23576B", mb: 1 }}>
              The Three-I Matrix 
            </Typography>
            <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
              Within RURACTIVE, the Interest–Influence–Inclusion Matrix provides a structured framework for assessing stakeholders based on their interest, Influence and Inclusion.
            </Typography>

            <Box className={styles.threeICardGrid}>
              {/* Interest */}
              <Box className={styles.threeICard}>
                <Typography className={styles.threeICardEyebrow}>Dimension</Typography>
                <Typography variant="h6" className={styles.threeICardTitle}>
                  Interest
                </Typography>
                <Typography variant="body2" className={styles.threeICardDesc}>
                  How strongly the stakeholder is affected by, or interested in, the selected RDD.
                </Typography>
                <Button
                  className={styles.threeILearnMore}
                  onClick={() => setOpenInterestPopup(true)}
                >
                  Learn more about Interest →
                </Button>
              </Box>

              {/* Influence */}
              <Box className={styles.threeICard}>
                <Typography className={styles.threeICardEyebrow}>Dimension</Typography>
                <Typography variant="h6" className={styles.threeICardTitle}>
                  Influence
                </Typography>
                <Typography variant="body2" className={styles.threeICardDesc}>
                  The stakeholder's ability to affect policies, processes, resources and activities related to the project.
                </Typography>
                <Button
                  className={styles.threeILearnMore}
                  onClick={() => setOpenInfluencePopup(true)}
                >
                  Learn more about Influence →
                </Button>
              </Box>

              {/* Inclusion Priority */}
              <Box className={styles.threeICard}>
                <Typography className={styles.threeICardEyebrow}>Dimension</Typography>
                <Typography variant="h6" className={styles.threeICardTitle}>
                  Inclusion Priority
                </Typography>
                <Typography variant="body2" className={styles.threeICardDesc}>
                  The degree to which a stakeholder should be prioritised because they belong to a group at risk of exclusion or underrepresentation.
                </Typography>
                <Button
                  className={styles.threeILearnMore}
                  onClick={() => setOpenInclusionPopup(true)}
                >
                  Learn more about Inclusion priority →
                </Button>
              </Box>
            </Box>

          </Box>

          {/* ── DATA ENTRY PANEL (do) ── */}
          <Box className={styles.phaseDataPanel}>
            <Box className={styles.phaseSectionLabel}>
              <Box className={styles.phaseSectionDot} />
              <Box>
                <Typography className={styles.phaseSectionTitle}>Analyse</Typography>
                <Typography className={styles.phaseSectionSubtitle}>
                  How to prioritise stakeholders
                </Typography>
              </Box>
            </Box>

            <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
              Your stakeholder position in the Three-I Matrix provides a starting point for deciding how each stakeholder should be involved.
            </Typography>

            <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
              The matrix has four levels of involvement, from lower to higher engagement:
            </Typography>

            <Box className={styles.involvementGrid}>
              {[
                {
                  level: "CONSULT",
                  desc: "Stakeholders with low interest and high influence.",
                  detail: "They may not be interested in actively participating, but their influence can be important for the RIE.",
                },
                {
                  level: "COLLABORATE",
                  desc: "Stakeholders with high interest and high influence.",
                  detail: "They are likely to be among the most valuable stakeholders for active participation in the RIE.",
                },
                {
                  level: "INFORM",
                  desc: "Stakeholders with low interest and low influence.",
                  detail: "They should be kept informed about the RIE and its activities.",
                },
                {
                  level: "INVOLVE",
                  desc: "Stakeholders with high interest and low influence.",
                  detail: "They are interested in the RIE and can contribute to the process, even if they have limited capacity to influence policies, resources or activities.",
                }
              ].map(({ level, desc, detail }) => (
                <Box key={level} className={styles.involvementCard}>
                  <Typography className={styles.involvementLabel}>
                    {level}
                  </Typography>
                  <Typography className={styles.involvementDesc}>
                    {desc}
                  </Typography>
                  <Typography className={styles.involvementDetail}>
                    {detail}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box className={styles.inclusionNote}>
              <Typography className={styles.inclusionNoteTitle}>
                Inclusion can change the priority
              </Typography>
              <Typography className={styles.inclusionNoteText} sx={{ mb: 1 }}>
                The matrix is not the only factor you should consider.
              </Typography>
              <Typography className={styles.inclusionNoteText} sx={{ mb: 1 }}>
                Stakeholders identified with a high inclusion priority should receive particular attention, even when their interest or influence appears relatively low.
              </Typography>
              <Typography className={styles.inclusionNoteText}>
                RURACTIVE uses inclusion as a concrete criterion for stakeholder prioritisation. Stakeholders at risk of exclusion or underrepresentation can therefore be "pushed up" to a higher level of involvement, usually towards Involve or Collaborate.
              </Typography>
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 700, color: "#23576B", mt: 3, mb: 1 }}>
              Prioritise your stakeholders
            </Typography>
            <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
              Review your stakeholder cards and assign each stakeholder to the appropriate level of involvement.
            </Typography>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px dashed rgba(35, 87, 107, 0.25)",
                backgroundColor: "rgba(35, 87, 107, 0.03)",
                mb: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "#23576B", fontWeight: 700, mb: 0.5 }}>
                Drag and drop
              </Typography>
              <Typography variant="body2" sx={{ color: "#7898A4" }}>
                Drag each stakeholder card into the level of involvement that best matches their interest and influence.
              </Typography>
            </Box>

            <Box className={styles.redDotCallout} sx={{ mt: 2 }}>
              <Box className={styles.redDotCircle} />
              <Typography className={styles.redDotText}>
                Red dot = stakeholder identified with high inclusion priority
              </Typography>
            </Box>

          </Box>

          {/* =========================
            WHITEBOARD
          ========================= */}
          <StakeholderWhiteboard
            stakeholders={stakeholders}
            domains={domains}
            onPositionChange={(stakeholder, { interest, influence }) => {
              setStakeholders((prev) =>
                prev.map((item) =>
                  item.id === stakeholder.id
                    ? { ...item, interest, influence }
                    : item
                )
              );

              dispatch(
                updateStakeholderPositionRequest({
                  projectId,
                  stakeholderId: stakeholder.id,
                  interest,
                  influence,
                })
              );
            }}
          />
        </Box>

        <FigmaBlueDivider />

        {/* =========================
           FILTERED STAKEHOLDERS
        ========================= */}

        <Box sx={{ mt: 6 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, mb: 1, color: "#23576B" }}
          >
            <PhaseArrow />
            Phase 1.3: Stakeholder Prioritisation
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: "#23576B", mb: 0.5 }}>
              Review your stakeholder priorities
            </Typography>
            <Typography variant="body1" sx={{ color: "#7898A4", mb: 2.5 }}>
              Before completing this phase, check your stakeholder landscape.
            </Typography>

            <Box className={styles.checklistPanel}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {[
                  {
                    title: "High-interest stakeholders are identified",
                    desc: "Stakeholders who are interested in the RIE should have an appropriate opportunity to participate.",
                  },
                  {
                    title: "Influential stakeholders are considered",
                    desc: "Stakeholders with the ability to affect policies, resources, processes or activities should not be overlooked.",
                  },
                  {
                    title: "Inclusion is reflected",
                    desc: "Stakeholders with high inclusion priority should receive particular attention, even when their interest or influence is lower.",
                  },
                  {
                    title: "No stakeholder is automatically excluded",
                    desc: "Prioritisation determines how you engage stakeholders, not whether they can participate.",
                  },
                ].map(({ title, desc }) => (
                  <Box key={title} className={styles.checklistItem}>
                    <Box className={styles.checklistIcon}>
                      <svg
                        className={styles.checklistIconSvg}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="white"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </Box>
                    <Box>
                      <Typography className={styles.checklistItemTitle}>
                        {title}
                      </Typography>
                      <Typography className={styles.checklistItemDesc}>
                        {desc}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "#23576B", mb: 1 }}
          >
            Your Prioritised Stakeholder List
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#7898A4", mb: 3 }}
          >
            Stakeholder involvement overview
          </Typography>

          <Box
            sx={{
              p: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "#E57373",
              backgroundColor: "#FFF5F5",
              boxShadow: 1,
            }}
          >

            <Box
              sx={{
                maxHeight: 350, //scrolling
                overflowY: stakeholders.length > 3 ? "auto" : "visible",
                pr: 1,
              }}
            >


              {/* HIGH PRIORITY */}
              <Typography sx={{ fontWeight: 600, mb: 1 }}>
                High Priority
              </Typography>

              {topStakeholders.map((s, index) => {
                const domain = domains.find(
                  (d) => String(d.id) === String(s.domain_id)
                );
                const rdd = rdds.find(
                  (r) => String(r.id) === String(s.rdd_id)
                );

                return (
                  <Box
                    key={s.id || index}
                    sx={{
                      p: 2.5,
                      mb: 2,
                      display: "flex",
                      boxShadow: 1,
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderRadius: "10px",
                      border: "1px solid #ddd",
                      borderColor: domain?.color,
                      backgroundColor: "white",
                    }}
                  >
                    {/* LEFT */}
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: "#23576B",
                        }}
                      >
                        {`${s.first_name || ""} ${s.last_name || ""}`.trim() ||
                          "Unnamed stakeholder"}
                      </Typography>

                      <Typography variant="body2" sx={{ color: "#23576B" }}>
                        {[
                          domain?.name || "No domain",
                          s.age && `Age: ${s.age}`,
                          s.gender,
                          `Interest: ${getLevel(s.interest)}`,
                          `Influence: ${getLevel(s.influence)}`,
                        ]
                          .filter(Boolean)
                          .join(" | ")}
                      </Typography>
                    </Box>

                    {/* RIGHT */}
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <PrimaryOutlinedButton
                        onClick={() => {
                          setFormData({ ...s });
                          setCurrentIndex(index);
                          setOpenModal(true);
                        }}
                      >
                        View Details
                      </PrimaryOutlinedButton>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box
            sx={{
              mt: 4,
              p: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "#90A4AE",
              backgroundColor: "#F7FAFC",
              boxShadow: 1,
            }}
          >
            {/* 🔽 LOWER PRIORITY */}
            <Typography sx={{ fontWeight: 600, mb: 1 }}>
              Lower Priority
            </Typography>

            <Box
              sx={{
                maxHeight: 350, //scrolling
                overflowY: stakeholders.length > 3 ? "auto" : "visible",
                pr: 1,
              }}
            >

              {bottomStakeholders.map((s, index) => {
                const domain = domains.find(
                  (d) => String(d.id) === String(s.domain_id)
                );
                const rdd = rdds.find(
                  (r) => String(r.id) === String(s.rdd_id)
                );

                return (
                  <Box
                    key={s.id || index}
                    sx={{
                      p: 2.5,
                      mb: 2,
                      display: "flex",
                      boxShadow: 1,
                      justifyContent: "space-between",
                      alignItems: "center",
                      borderRadius: "10px",
                      border: "1px solid #ddd",
                      borderColor: domain?.color,
                      backgroundColor: "white",
                    }}
                  >
                    {/* LEFT */}
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          color: "#23576B",

                        }}
                      >
                        {`${s.first_name || ""} ${s.last_name || ""}`.trim() ||
                          "Unnamed stakeholder"}
                      </Typography>

                      <Typography variant="body2" sx={{ color: "#23576B" }}>
                        {[
                          domain?.name || "No domain",
                          s.age && `Age: ${s.age}`,
                          s.gender,
                          `Interest: ${getLevel(s.interest)}`,
                          `Influence: ${getLevel(s.influence)}`,
                        ]
                          .filter(Boolean)
                          .join(" | ")}
                      </Typography>
                    </Box>

                    {/* RIGHT */}
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <PrimaryOutlinedButton
                        onClick={() => {
                          setFormData({ ...s });
                          setCurrentIndex(index);
                          setOpenModal(true);
                        }}
                      >
                        View Details
                      </PrimaryOutlinedButton>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* =========================
            LOCAL TASK FORCE
          ========================= */}
        <Box sx={{ mt: 6 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#23576B", mb: 2 }}>
            Local Task Force (LTF)
          </Typography>
        </Box>

        {/* ── INFO PANEL (learn) ── */}
        <Box className={styles.phaseInfoPanel}>
          <Box className={styles.phaseSectionLabel}>
            <Box className={styles.phaseSectionDot} />
            <Box>
              <Typography className={styles.phaseSectionTitle}>Learn</Typography>
              <Typography className={styles.phaseSectionSubtitle}>
                What is the Local Task Force?
              </Typography>
            </Box>
          </Box>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            The Local Task Force (LTF) is the core working group of the RIE. It is made up of selected stakeholders who are most actively involved in the RIE. They should bring relevant expertise or interest in one or more Rural Development Drivers, or have the potential to benefit from the project's co-development activities.
          </Typography>

          <Button
            className={styles.threeILearnMore}
            onClick={() => setOpenLtfWhyPopup(true)}
            sx={{ mb: 3 }}
          >
            Learn more about why the LTF is important →
          </Button>

          {/* Role cards */}
          <Box className={styles.perspectiveGrid} sx={{ mb: 3 }}>
            <Box className={styles.perspectiveCard}>
              <Typography variant="h6" className={styles.perspectiveCardTitle}>
                The LTF will support the RIE by:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {[
                  "Helping identify local challenges and needs",
                  "Supporting the engagement of other stakeholders",
                  "Contributing to the co-development activities",
                  "Supporting the RIE Coordinator in the following stages of the methodology",
                ].map((item) => (
                  <Box component="li" key={item} sx={{ mb: 0.5 }}>
                    <Typography variant="body2" className={styles.perspectiveCardText}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box className={styles.perspectiveCard}>
              <Typography variant="h6" className={styles.perspectiveCardTitle}>
                Who should be part of the LTF?
              </Typography>
              <Typography variant="body2" className={styles.perspectiveCardText} sx={{ mb: 1 }}>
                When selecting members, consider stakeholders who:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {[
                  "Have relevant expertise related to one or more selected RDDs",
                  "Have a strong interest and are motivated to contribute",
                  "Could benefit from the co-development activities and solutions",
                  "Represent different stakeholder domains, RDDs and community perspectives",
                  "Support inclusion — include people from groups at risk of exclusion or underrepresentation",
                ].map((item) => (
                  <Box component="li" key={item} sx={{ mb: 0.5 }}>
                    <Typography variant="body2" className={styles.perspectiveCardText}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 1, fontStyle: "italic" }}>
            Where possible, and depending on the local context, the LTF should include representation from each selected RDD and each of the four stakeholder domains.
          </Typography>

          {/* Recommended composition */}
          <Box sx={{
            mt: 2,
            mb: 2,
            p: 2,
            borderRadius: "16px",
            border: "1px solid rgba(53, 179, 171, 0.2)",
            borderLeft: "4px solid #35B3AB",
            background: "linear-gradient(135deg, rgba(53, 179, 171, 0.07), rgba(35, 87, 107, 0.05))",
          }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#23576B", mb: 0.75 }}>
              Recommended composition
            </Typography>
            <Typography variant="body2" sx={{ color: "#7898A4", mb: 1.5 }}>
              The LTF should be composed of 4–7 stakeholders, including:
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
              {[
                { role: "The coordinator", count: "1" },
                { role: "At least 1 member from a socially excluded group", count: "≥1" },
                { role: "Other prioritised stakeholders", count: "3–4" },
              ].map(({ role, count }) => (
                <Box
                  key={role}
                  sx={{
                    flex: 1,
                    py: 1.5,
                    px: 1,
                    borderRadius: 2,
                    background: "#fff",
                    border: "1px solid rgba(53, 179, 171, 0.18)",
                    textAlign: "center",
                    boxShadow: "0 2px 8px rgba(53, 179, 171, 0.08)",
                  }}
                >
                  <Typography sx={{ color: "#35B3AB", fontWeight: 800, fontSize: "1.25rem", lineHeight: 1, mb: 0.3 }}>
                    {count}
                  </Typography>
                  <Typography sx={{ color: "#23576B", fontWeight: 600, fontSize: "0.78rem", lineHeight: 1.3 }}>
                    {role}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Typography variant="body2" sx={{ color: "#7898A4", fontStyle: "italic" }}>
              It also recommends aiming for a gender-balanced composition, with at least 40% representation of one gender and 60% of the other. These are inclusion and representation targets to guide your selection. Consider your local context when composing the LTF.
            </Typography>

            <Button
              className={styles.threeILearnMore}
              onClick={() => setOpenLtfCompositionPopup(true)}
              sx={{ mt: 1 }}
            >
              Learn more about LTF composition →
            </Button>
          </Box>

          
        </Box>

        {/* ── DATA ENTRY PANEL (do) ── */}
        <Box className={styles.phaseDataPanel}>
          <Box className={styles.phaseSectionLabel}>
            <Box className={styles.phaseSectionDot} />
            <Box>
              <Typography className={styles.phaseSectionTitle}>Assemble</Typography>
              <Typography className={styles.phaseSectionSubtitle}>
                Select your Local Task Force members
              </Typography>
            </Box>
          </Box>

          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            Review your prioritised stakeholders and select the members that will form the Local Task Force. Consider the recommended composition and criteria above.
          </Typography>

          <LocalTaskForceSection
            stakeholders={stakeholders}
            setStakeholders={setStakeholders}
            localTaskForceMembers={localTaskForceMembers}
            rdds={rdds}
            roles={roles}
            projectId={projectId}
            dispatch={dispatch}
            onAddNewMember={handleAddLocalTaskForceMember}
            setFormData={setFormData}
            setCurrentIndex={setCurrentIndex}
            setOpenModal={setOpenModal}
          />
        </Box>

      </Box>

      {/* ── GREU POPUP ── */}
      <Dialog
        open={openGreuPopup}
        onClose={() => setOpenGreuPopup(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#23576B" }}>
          Groups at Risk of Exclusion (GREU)
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#23576B", mb: 1 }}>
            What are Groups at Risk of Exclusion?
          </Typography>
          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            Groups at Risk of Exclusion (GREU) are groups that may be historically
            underrepresented or excluded from decision-making and development
            processes because of social, demographic, economic, physical or other
            intersecting factors.
          </Typography>
          <Typography variant="body1" sx={{ color: "#7898A4", mb: 1 }}>
            Consider factors such as:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 2 }}>
            {["Age", "Gender", "Physical disabilities or impairments", "Ethnic origins",
              "Religion or beliefs", "Language", "Socioeconomic circumstances",
              "Other intersecting characteristics"].map((item) => (
              <Box component="li" key={item} sx={{ mb: 0.5 }}>
                <Typography variant="body1" sx={{ color: "#7898A4" }}>{item}</Typography>
              </Box>
            ))}
          </Box>
          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            The relevant groups can vary depending on the local context.
          </Typography>
          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2, fontStyle: "italic" }}>
            Think locally: a group that is at risk of exclusion in one territory may
            not face the same barriers in another. Your stakeholder identification
            should therefore consider the specific characteristics and challenges of
            your community.
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#23576B", mb: 1 }}>
            Examples of groups to consider
          </Typography>
          <Typography variant="body1" sx={{ color: "#7898A4" }}>
            Young people · Older people · People with disabilities or impairments ·
            Migrants · Linguistic, ethnic or religious minorities · Long-term
            unemployed people · LGBTQIA+ people
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenGreuPopup(false)}
            sx={{ textTransform: "none", color: "#35B3AB", fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── TRANSVERSAL STAKEHOLDERS POPUP ── */}
      <Dialog
        open={openTransversalPopup}
        onClose={() => setOpenTransversalPopup(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#23576B" }}>
          Transversal Stakeholders
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#23576B", mb: 1 }}>
            What are transversal stakeholders?
          </Typography>
          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            Some stakeholders are relevant to several RDDs, rather than only one.
          </Typography>
          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            They may have a role in local politics, policies or development, or be
            strongly connected to the cross-cutting priorities of climate change
            adaptation and mitigation, biodiversity, or social justice and inclusion.
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#23576B", mb: 1 }}>
            Examples
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 2 }}>
            {["Municipalities", "Regional offices", "Schools and educational institutions"].map((item) => (
              <Box component="li" key={item} sx={{ mb: 0.5 }}>
                <Typography variant="body1" sx={{ color: "#7898A4" }}>{item}</Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenTransversalPopup(false)}
            sx={{ textTransform: "none", color: "#35B3AB", fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── INTEREST POPUP ── */}
      <Dialog
        open={openInterestPopup}
        onClose={() => setOpenInterestPopup(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#23576B" }}>
          What does "Interest" mean?
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            Interest describes how directly a stakeholder is affected by, or connected to,
            a Rural Development Driver.
          </Typography>

          <Box className={styles.popupLevelCard}>
            <Typography className={styles.popupLevelLabel}>High interest</Typography>
            <Typography variant="body1" className={styles.popupLevelDesc}>
              The stakeholder is directly, personally or as part of a group, affecting
              and affected by the RDD.
            </Typography>
          </Box>

          <Box className={styles.popupLevelCard}>
            <Typography className={styles.popupLevelLabel}>Medium interest</Typography>
            <Typography variant="body1" className={styles.popupLevelDesc}>
              The stakeholder is partially affecting and affected by the RDD.
            </Typography>
          </Box>

          <Box className={styles.popupLevelCard}>
            <Typography className={styles.popupLevelLabel}>Low interest</Typography>
            <Typography variant="body1" className={styles.popupLevelDesc}>
              The stakeholder is not affecting or affected by the RDD.
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: "#7898A4", mt: 2, fontStyle: "italic" }}>
            Consider the stakeholder in relation to the specific RDD, rather than
            assuming that their level of interest is the same across all RDDs.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenInterestPopup(false)}
            sx={{ textTransform: "none", color: "#35B3AB", fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── INFLUENCE POPUP ── */}
      <Dialog
        open={openInfluencePopup}
        onClose={() => setOpenInfluencePopup(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#23576B" }}>
          What does "Influence" mean?
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            Influence describes the stakeholder's ability to affect policies, processes,
            resources or activities related to the RIE.
          </Typography>

          <Box className={styles.popupLevelCard}>
            <Typography className={styles.popupLevelLabel}>High influence</Typography>
            <Typography variant="body1" className={styles.popupLevelDesc}>
              The stakeholder has a direct impact and the power to modify or deliver impact.
            </Typography>
          </Box>

          <Box className={styles.popupLevelCard}>
            <Typography className={styles.popupLevelLabel}>Medium influence</Typography>
            <Typography variant="body1" className={styles.popupLevelDesc}>
              The stakeholder has little or no direct impact but has the power to influence impact.
            </Typography>
          </Box>

          <Box className={styles.popupLevelCard}>
            <Typography className={styles.popupLevelLabel}>Low influence</Typography>
            <Typography variant="body1" className={styles.popupLevelDesc}>
              The stakeholder has no impact on policies, processes or resources.
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: "#7898A4", mt: 2, fontStyle: "italic" }}>
            Influence is about the stakeholder's capacity to affect the process, not
            simply their visibility or popularity in the community.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenInfluencePopup(false)}
            sx={{ textTransform: "none", color: "#35B3AB", fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── INCLUSION PRIORITY POPUP ── */}
      <Dialog
        open={openInclusionPopup}
        onClose={() => setOpenInclusionPopup(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#23576B" }}>
          What does "Inclusion priority" mean?
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            Inclusion priority is used to make sure that stakeholders who are at risk
            of exclusion or underrepresentation are not overlooked because of lower
            levels of influence or interest.
          </Typography>

          <Box className={styles.popupLevelCard}>
            <Typography className={styles.popupLevelLabel}>High inclusion priority</Typography>
            <Typography variant="body1" className={styles.popupLevelDesc}>
              The stakeholder belongs to a group at risk of exclusion or underrepresentation
              and could directly benefit from the co-development activities.
            </Typography>
          </Box>

          <Box className={styles.popupLevelCard}>
            <Typography className={styles.popupLevelLabel}>Medium inclusion priority</Typography>
            <Typography variant="body1" className={styles.popupLevelDesc}>
              The stakeholder belongs to such a group and could partially benefit.
            </Typography>
          </Box>

          <Box className={styles.popupLevelCard}>
            <Typography className={styles.popupLevelLabel}>Low inclusion priority</Typography>
            <Typography variant="body1" className={styles.popupLevelDesc}>
              The stakeholder does not belong to a group at risk of exclusion or
              underrepresentation, and benefiting from the co-development activities
              is not a priority.
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: "#7898A4", mt: 2, fontStyle: "italic" }}>
            A high inclusion priority can "push" a stakeholder towards a higher level
            of involvement, even when their interest or influence is relatively low.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenInclusionPopup(false)}
            sx={{ textTransform: "none", color: "#35B3AB", fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* ── LTF WHY POPUP ── */}
      <Dialog
        open={openLtfWhyPopup}
        onClose={() => setOpenLtfWhyPopup(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#23576B" }}>
          The LTF as the core working group
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            The Local Task Force provides a group of stakeholders who can work closely with the RIE Coordinator throughout the process.
          </Typography>
          <Typography variant="body1" sx={{ color: "#7898A4", mb: 1 }}>
            LTF members can help:
          </Typography>
          <Box component="ul" sx={{ color: "#7898A4", pl: 3, mb: 2 }}>
            <li><Typography variant="body1" component="span" sx={{ color: "#7898A4" }}>Identify local challenges and needs</Typography></li>
            <li><Typography variant="body1" component="span" sx={{ color: "#7898A4" }}>Reach and engage other stakeholders</Typography></li>
            <li><Typography variant="body1" component="span" sx={{ color: "#7898A4" }}>Contribute knowledge and expertise</Typography></li>
            <li><Typography variant="body1" component="span" sx={{ color: "#7898A4" }}>Support co-development activities</Typography></li>
            <li><Typography variant="body1" component="span" sx={{ color: "#7898A4" }}>Help organise and participate in activities during the following steps</Typography></li>
          </Box>
          <Typography variant="body1" sx={{ color: "#7898A4" }}>
            The LTF is the group that will support the RIE Coordinator in contacting and engaging stakeholders in Step 2 and in setting up stakeholder empowerment activities in Step 3.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenLtfWhyPopup(false)}
            sx={{ textTransform: "none", color: "#35B3AB", fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── LTF COMPOSITION POPUP ── */}
      <Dialog
        open={openLtfCompositionPopup}
        onClose={() => setOpenLtfCompositionPopup(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#23576B" }}>
          Building a representative LTF
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ color: "#7898A4", mb: 2 }}>
            When selecting the LTF, consider the following:
          </Typography>

          <Box className={styles.popupLevelCard}>
            <Typography className={styles.popupLevelLabel}>4–7 stakeholders</Typography>
            <Typography variant="body1" className={styles.popupLevelDesc}>
              The LTF should have four to seven stakeholders.
            </Typography>
          </Box>

          <Box className={styles.popupLevelCard}>
            <Typography className={styles.popupLevelLabel}>RDD representation</Typography>
            <Typography variant="body1" className={styles.popupLevelDesc}>
              When possible, consider having at least one member connected to each selected RDD.
            </Typography>
          </Box>

          <Box className={styles.popupLevelCard}>
            <Typography className={styles.popupLevelLabel}>Stakeholder domains</Typography>
            <Typography variant="body1" className={styles.popupLevelDesc}>
              When possible, consider representation from each of the four stakeholder domains.
            </Typography>
          </Box>

          <Box className={styles.popupLevelCard}>
            <Typography className={styles.popupLevelLabel}>Inclusion</Typography>
            <Typography variant="body1" className={styles.popupLevelDesc}>
              Strive to include at least one representative from a group at risk of exclusion or underrepresentation.
            </Typography>
          </Box>

          <Box className={styles.popupLevelCard}>
            <Typography className={styles.popupLevelLabel}>Gender balance</Typography>
            <Typography variant="body1" className={styles.popupLevelDesc}>
              Aim for at least 40% representation of one gender and 60% of the other.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenLtfCompositionPopup(false)}
            sx={{ textTransform: "none", color: "#35B3AB", fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </StepLayout>
  );
};

export default StepOnePage;